import { useTableActionContext } from '@aibees/elephant/context';
import { useInfiniteCampaigns } from '@aibees/elephant/hooks/integration/use-infinite-campaigns';
import { useInfiniteIntegration } from '@aibees/elephant/hooks/integration/use-infinite-integration';
import { DropDown, DropDownOption, Icon } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Button, Stack } from '@mui/material';
import { memo, useEffect } from 'react';
import { useIntegrationContext } from '../../context/integration-context';
import * as Styled from './styles';
import * as SX from './sx';
import type { ExportIntegrationNameProps as Props } from './types';
import { useConvertDataToDropDownOption } from './use-convert-data-to-drop-down-option';
import { useIntegrationDefaultItem } from './use-integration-default-item';

interface IntegrationDropDownItemProps {
  label: string;
  option: DropDownOption;
  selected?: DropDownOption['value'];
}

const IntegrationDropDownItem = memo(
  ({ label, option, selected }: IntegrationDropDownItemProps) => {
    const currentItem = option?.value?.id === selected?.id;

    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Icon name="key-outline" size={20} />
          {label}
        </Stack>

        {currentItem && <Icon name="check" size={20} color="primary.400" />}
      </Stack>
    );
  }
);

IntegrationDropDownItem.displayName = 'IntegrationDropDownItem';

export const ExportIntegrationName = memo(
  ({ className, expandedAccordion, setExpandedAccordion }: Props) => {
    const { refetch } = useInfiniteCampaigns();
    const {
      exportDialogState,
      integrationConnectDialogState,
      integrationConfig,
    } = useTableActionContext();
    const { setSelectedIntegration, selectedIntegration } =
      useIntegrationContext();
    const { integration } = useInfiniteIntegration(integrationConfig?.type);

    const defaultItem = useIntegrationDefaultItem(integration);

    const options = useConvertDataToDropDownOption(integration);
    const handleChange = (e: DropDownOption['value']) => {
      setSelectedIntegration(e);
      refetch();
    };

    const handleAddIntegration = () => {
      integrationConnectDialogState.onTrue();
      exportDialogState.onFalse();
    };

    useEffect(() => {
      if (defaultItem && !selectedIntegration) {
        setSelectedIntegration(defaultItem);
      }
    }, [
      defaultItem,
      setSelectedIntegration,
      exportDialogState.value,
      selectedIntegration,
    ]);

    return (
      <Styled.Wrapper
        id="export-integration-name"
        className={classnames('ExportIntegrationName', className)}
        sx={SX.root}
        title={`${integrationConfig?.name} Connection`}
        leftIcon="person-outline"
        description={
          !integration?.length ? null : (
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Icon name="check-circle-outline" color="success.600" size={16} />

              {selectedIntegration?.name}
            </Stack>
          )
        }
        expandedAccordion={expandedAccordion}
        setExpandedAccordion={setExpandedAccordion}
      >
        <Stack direction="row" gap={2} alignItems="center">
          <DropDown
            fullWidth
            options={options}
            size="small"
            withCheckmark
            disabled={!integration?.length}
            value={selectedIntegration}
            onChange={handleChange}
          />

          <Button
            variant="outlined"
            size="small"
            onClick={handleAddIntegration}
          >
            <Icon name="plus" />
          </Button>
        </Stack>
      </Styled.Wrapper>
    );
  }
);

ExportIntegrationName.displayName = 'ExportIntegrationName';
